import React, { useState, useCallback } from 'react';
import api from '../services/api';
import {
    Box, Typography, Paper, TextField, Button, CircularProgress, Chip, Collapse,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert,
    InputAdornment, IconButton
} from '@mui/material';
import { Search, AutoFixHigh, ExpandMore, ExpandLess, Close, DateRange } from '@mui/icons-material';
import { TAX_CAT_LABELS } from '../constants/taxCategories';
import { PERSONAL_CAT_LABELS } from '../constants/personalCategories';

function TransactionSearch({ year, defaultOpen = false, showDateRange = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [termsUsed, setTermsUsed] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const yr = year || new Date().getFullYear() - 1;

    const handleSearch = useCallback(() => {
        if (!query.trim()) return;
        setSearching(true);
        setResults(null);
        const params = { q: query.trim(), smart: true };
        if (startDate && endDate) {
            params.startDate = startDate;
            params.endDate = endDate;
        } else {
            params.year = yr;
        }
        api.get('/api/csv/search', { params })
            .then(res => {
                setResults(res.data.results || []);
                setTermsUsed(res.data.searchTerms || [query.trim()]);
            })
            .catch(err => {
                console.error('Search error:', err);
                setResults([]);
                setTermsUsed([query.trim()]);
            })
            .finally(() => setSearching(false));
    }, [query, yr, startDate, endDate]);

    const clearSearch = () => {
        setQuery('');
        setResults(null);
        setTermsUsed([]);
        setStartDate('');
        setEndDate('');
    };

    const total = results
        ? Math.abs(results.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))
        : 0;

    return (
        <Paper elevation={0} sx={{ borderRadius: 3, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <Box
                onClick={() => setOpen(prev => !prev)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5, cursor: 'pointer',
                    background: open ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
                    color: open ? '#fff' : 'text.primary',
                    transition: 'all 0.3s ease',
                    '&:hover': { backgroundColor: open ? undefined : 'rgba(102,126,234,0.04)' }
                }}
            >
                <Box sx={{ p: 0.8, borderRadius: 2, backgroundColor: open ? 'rgba(255,255,255,0.2)' : 'rgba(102,126,234,0.1)' }}>
                    <Search sx={{ color: open ? '#fff' : '#667eea', fontSize: 24 }} />
                </Box>
                <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight={700}>Smart Transaction Search</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        AI-powered — search "internet" to find Comcast, Xfinity, AT&T, etc.
                    </Typography>
                </Box>
                <AutoFixHigh sx={{ fontSize: 20, opacity: 0.7 }} />
                {open ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ p: 3, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Box display="flex" gap={1.5} alignItems="center" mb={showDateRange ? 1.5 : 2}>
                        <TextField
                            fullWidth size="small"
                            placeholder="Search transactions — e.g. internet, groceries, amazon, starbucks..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search sx={{ color: '#999' }} /></InputAdornment>,
                                endAdornment: query && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearSearch}><Close fontSize="small" /></IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button variant="contained" onClick={handleSearch}
                            disabled={searching || !query.trim()}
                            startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <AutoFixHigh />}
                            sx={{
                                minWidth: 140, height: 40, textTransform: 'none', fontWeight: 600,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' }
                            }}>
                            {searching ? 'Searching...' : 'AI Search'}
                        </Button>
                    </Box>
                    {showDateRange && (
                        <Box display="flex" gap={1.5} alignItems="center" mb={2}>
                            <DateRange sx={{ color: '#667eea', fontSize: 20 }} />
                            <TextField
                                size="small" type="date" label="From"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <Typography variant="body2" color="text.secondary">to</Typography>
                            <TextField
                                size="small" type="date" label="To"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            {(startDate || endDate) && (
                                <Chip
                                    label="Clear dates"
                                    size="small"
                                    onDelete={() => { setStartDate(''); setEndDate(''); }}
                                    sx={{ fontSize: '0.7rem', height: 24 }}
                                />
                            )}
                            {!startDate && !endDate && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Leave blank to search full year {yr}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {termsUsed.length > 1 && (
                        <Box display="flex" gap={0.5} flexWrap="wrap" mb={2}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, lineHeight: '24px' }}>
                                AI searched for:
                            </Typography>
                            {termsUsed.map((t, i) => (
                                <Chip key={i} label={t} size="small"
                                    sx={{ height: 22, fontSize: '0.7rem', backgroundColor: 'rgba(102,126,234,0.1)', color: '#667eea' }} />
                            ))}
                        </Box>
                    )}

                    {searching && (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={32} sx={{ color: '#667eea' }} />
                        </Box>
                    )}

                    {results !== null && !searching && (
                        results.length === 0 ? (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                No transactions found matching "{query}" for {startDate && endDate ? `${startDate} to ${endDate}` : yr}.
                            </Alert>
                        ) : (
                            <Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                                        Found {results.length} transaction{results.length !== 1 ? 's' : ''}
                                    </Typography>
                                    <Chip
                                        label={`Total: $${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                        size="small"
                                        sx={{ fontWeight: 700, fontSize: '0.8rem', backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                                    />
                                </Box>
                                <TableContainer sx={{ maxHeight: 420, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Description</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Source</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Amount</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Type</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }}>Category</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {results.map((r, i) => {
                                                const amt = parseFloat(r.amount) || 0;
                                                const catLabel = r.taxCategory && TAX_CAT_LABELS[r.taxCategory]
                                                    ? TAX_CAT_LABELS[r.taxCategory]
                                                    : r.personalCategory && PERSONAL_CAT_LABELS[r.personalCategory]
                                                        ? PERSONAL_CAT_LABELS[r.personalCategory]
                                                        : null;
                                                return (
                                                    <TableRow key={`${r.source}-${r.id}-${i}`} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                            {r.date ? new Date(r.date + 'T00:00:00').toLocaleDateString() : '—'}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 350 }}>
                                                            {r.description || r.merchantName || '—'}
                                                            {r.merchantName && r.description && r.merchantName !== r.description && (
                                                                <Typography variant="caption" display="block" color="text.disabled">
                                                                    {r.merchantName}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={r.source === 'bank' ? 'Bank' : r.accountName || 'Personal'}
                                                                size="small" sx={{
                                                                    height: 20, fontSize: '0.65rem',
                                                                    backgroundColor: r.source === 'bank' ? '#e3f2fd' : '#f3e5f5',
                                                                    color: r.source === 'bank' ? '#1565c0' : '#7b1fa2'
                                                                }} />
                                                        </TableCell>
                                                        <TableCell align="right" sx={{
                                                            fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap',
                                                            color: amt < 0 ? '#e53935' : '#43a047'
                                                        }}>
                                                            ${Math.abs(amt).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </TableCell>
                                                        <TableCell>
                                                            {r.type ? (
                                                                <Chip label={r.type} size="small" sx={{
                                                                    height: 20, fontSize: '0.65rem',
                                                                    backgroundColor: r.type === 'Business' ? '#e8f5e9' : '#fff3e0',
                                                                    color: r.type === 'Business' ? '#2e7d32' : '#e65100'
                                                                }} />
                                                            ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                        </TableCell>
                                                        <TableCell>
                                                            {catLabel ? (
                                                                <Chip label={catLabel} size="small" sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                                                            ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
}

export default TransactionSearch;
